// Box => button 1 : Do you have any questions? | Button 2 : Would you like to schedule a free consultation?
// Questions => Form
// Book => Do you want to meet during weekdays or weekends? => 10-4pm PST or After Hours?
import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import {
    TextField,
    Paper,
    Button,
    CircularProgress,
    makeStyles,
    Typography
} from '@material-ui/core'
import { green } from '@material-ui/core/colors'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios from 'axios'

const contactSchema = yup.object({
    first_name: yup
        .string('Enter your first name')
        .min(1, 'Name too Short')
        .max(25, 'Name too long')
        .required('First name is required'),
    last_name: yup
        .string('Enter your last name')
        .min(2, 'Name too short')
        .max(25, 'Name too long')
        .required('Last name is required'),
    company: yup
        .string('Enter company name')
        .min(5, 'Company name too Short')
        .max(25, 'Company name too long'),
    email: yup
        .string('Enter your email')
        .email('Enter a valid email')
        .required('Email is required'),
    phone_number: yup
        .string('Enter your phone number')
        .min(10, 'Phone number is not valid')
        .max(11, 'Phone number is not valid'),
    website: yup
        .string('Please enter your web address')
        .min(5, 'Company name too Short')
        .max(25, 'Company name too long'),
    message: yup
        .string('Please enter your message')
        .min(10, 'Message is too short')
        .max(500, 'Message is too long')
        .required('Message is required')
})

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(4),
        backgroundColor: '#ededed',
        maxWidth: '100%'
    },
    buttonWrapper: {
        margin: theme.spacing(4, 0, 0),
        position: 'relative'
    },
    button: {
        backgroundColor: 'rgb(0, 162, 255)'
    },
    buttonProgress: {
        color: green[500],
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12
    }
}))

const ContactForm = () => {
    const [loading, setLoading] = useState(false)
    const [sent, setSent] = useState(false)
    const classes = useStyles()
    const formik = useFormik({
        initialValues: {
            first_name: '',
            last_name: '',
            company: '',
            email: '',
            phone_number: '',
            website: '',
            message: ''
        },
        validationSchema: contactSchema,
        onSubmit: async (values) => {
            setLoading(true)
            await axios
                .post(`${process.env.NEXT_PUBLIC_SERVER_API}/messages`, {
                    ...values,
                    phone_number: parseInt(phone_number)
                })
                .then((res) => {
                    if (res.status === 200) {
                        setSent(true)
                    }
                })
                .catch((err) => {
                    setLoading(false)
                })
        }
    })

    const buttonClassname = clsx(classes.button, {
        [classes.buttonSucces]: sent
    })

    useEffect(() => {
        if (sent) {
            console.log('set to reload')
            setTimeout(() => {
                window.location.reload()
            }, 6000)
        }
    }, [sent])

    if (sent) {
        return (
            <Paper elevation={3} className={classes.root}>
                <Typography
                    className={classes.header}
                    variant="h5"
                    gutterBottom
                >
                    Thank you for your feedback!
                </Typography>
                <Typography className={classes.body} variation="body1">
                    New Evolution Digital thrives off of feedback and ideas in a
                    creative way. If you have questions, comments, or concerns
                    please let us know so that we can address them. If you are
                    ready to set up an initial consultation, feel free to use
                    the "Schedule time with me" button and we would be happy to
                    meet with you.
                </Typography>
            </Paper>
        )
    } else
        return (
            <Paper elevation={3} className={classes.root}>
                <form onSubmit={formik.handleSubmit}>
                    <TextField
                        fullWidth
                        id="first_name"
                        name="first_name"
                        label="First Name"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.first_name &&
                            Boolean(formik.errors.first_name)
                        }
                        helperText={
                            formik.touched.first_name &&
                            formik.errors.first_name
                        }
                        disabled={loading}
                    />
                    <TextField
                        fullWidth
                        id="last_name"
                        name="last_name"
                        label="Last Name"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.last_name &&
                            Boolean(formik.errors.last_name)
                        }
                        helperText={
                            formik.touched.last_name && formik.errors.last_name
                        }
                        disabled={loading}
                    />
                    <TextField
                        fullWidth
                        id="company"
                        name="company"
                        label="Company Name"
                        value={formik.values.company}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.company &&
                            Boolean(formik.errors.company)
                        }
                        helperText={
                            formik.touched.company && formik.errors.company
                        }
                        disabled={loading}
                    />
                    <TextField
                        fullWidth
                        id="email"
                        name="email"
                        label="Email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                        disabled={loading}
                    />

                    <TextField
                        fullWidth
                        id="phone_number"
                        name="phone_number"
                        label="Phone Number"
                        value={formik.values.phone_number}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.phone_number &&
                            Boolean(formik.errors.phone_number)
                        }
                        helperText={
                            formik.touched.phone_number &&
                            formik.errors.phone_number
                        }
                        disabled={loading}
                    />
                    <TextField
                        fullWidth
                        id="website"
                        name="website"
                        label="Website"
                        value={formik.values.website}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.website &&
                            Boolean(formik.errors.website)
                        }
                        helperText={
                            formik.touched.website && formik.errors.website
                        }
                        disabled={loading}
                    />

                    <TextField
                        fullWidth
                        id="message"
                        name="message"
                        label="Message"
                        value={formik.values.message}
                        onChange={formik.handleChange}
                        multiline
                        rows={4}
                        error={
                            formik.touched.message &&
                            Boolean(formik.errors.message)
                        }
                        helperText={
                            formik.touched.message && formik.errors.message
                        }
                        disabled={loading}
                    />

                    <div className={classes.buttonWrapper}>
                        <Button
                            className={buttonClassname}
                            color="primary"
                            variant="contained"
                            fullWidth
                            type="submit"
                            disabled={loading}
                            size="large"
                        >
                            Submit
                        </Button>
                        {loading && (
                            <CircularProgress
                                size={24}
                                className={classes.buttonProgress}
                            />
                        )}
                    </div>
                </form>
            </Paper>
        )
}

export default ContactForm
